/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Profile } from 'teamProfiles/_types';
import { mergeRegister } from '@lexical/utils';
import routesConfig from 'constants/routesConfig.json';
import {
  $createRangeSelection,
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $setSelection,
  $isRangeSelection,
  $isTextNode,
  type LexicalNode,
  COMMAND_PRIORITY_NORMAL,
  KEY_DOWN_COMMAND,
  SELECTION_CHANGE_COMMAND,
  TextNode,
} from 'lexical';
import { $createMentionNode, $isMentionNode } from './MentionNode';

type DropdownRenderer = (
  anchorRect: DOMRect,
  query: string,
  select: (profile: Profile | null) => void,
  entityExtractionId: string | undefined,
  clickedMentionLabel: string | undefined,
  clickedProfileId: string | undefined
) => React.ReactNode;

type ViewModeProfileSelect = (
  profile: Profile,
  entityExtractionId: string | undefined
) => void;

type Props = {
  disabled: boolean;
  dropdownRenderer: DropdownRenderer;
  onViewModeProfileSelect?: ViewModeProfileSelect;
};

export default function MentionPlugin({
  disabled,
  dropdownRenderer,
  onViewModeProfileSelect,
}: Props) {
  const [editor] = useLexicalComposerContext();
  const history = useHistory();
  const [query, setQuery] = useState('');
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const lastSelectionRef = useRef<{
    anchorKey: string;
    anchorOffset: number;
    focusKey: string;
    focusOffset: number;
  } | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const lastTriggerRef = useRef<{
    nodeKey: string;
    start: number;
    end: number;
  } | null>(null);
  const lastClickedMentionKeyRef = useRef<string | null>(null);
  const lastClickedEntityExtractionIdRef = useRef<string | null>(null);
  const lastClickedMentionLabelRef = useRef<string | null>(null);
  const lastClickedMentionIdRef = useRef<string | null>(null);
  const ignoreNextSelectionRef = useRef(false);

  const findAnyMention = () => {
    const root = editor.getEditorState().read(() => $getSelection()?.getNodes()[0]?.getTopLevelElement());
    let found: LexicalNode | null = null;
    editor.getEditorState().read(() => {
      const walk = (node: LexicalNode) => {
        if (found) return;
        if ($isMentionNode(node)) {
          found = node;
          return;
        }
        if ('getChildren' in node) {
          // @ts-expect-error loose walk
          node.getChildren().forEach((child: LexicalNode) => walk(child));
        }
      };
      walk((editor.getEditorState().read(() => $getSelection()?.getNodes()[0]?.getTopLevelElement())) || editor.getEditorState().read(() => $getSelection()?.getNodes()[0]) || editor.getEditorState().read(() => null));
    });
    return found;
  };

  const getAnchorRect = (range: Range): DOMRect | null => {
    const primary = range.getBoundingClientRect();
    if (
      primary &&
      (primary.width > 0 || primary.height > 0 || primary.x !== 0 || primary.y !== 0)
    ) {
      return primary;
    }
    const node = range.startContainer;
    const el =
      node instanceof Text
        ? node.parentElement
        : node instanceof HTMLElement
          ? node
          : null;
    return el ? el.getBoundingClientRect() : null;
  };

  // Track selection changes to update anchor position for the dropdown
  useEffect(
    () =>
      mergeRegister(
        editor.registerRootListener((rootElement) => {
          if (!rootElement) return undefined;
          rootRef.current = rootElement;
          const handleClick = (e: MouseEvent) => {
            const target = (e.target as HTMLElement | null)?.closest(
              '[data-mention-key]'
            ) as HTMLElement | null;
            if (!target?.dataset?.mentionKey) return;

            const profileId = target.dataset.mentionId;
            const hasLinkedProfile =
              target.dataset.hasProfile === 'true' &&
              !!profileId &&
              !profileId.startsWith('entity-');

            if (disabled) {
              if (hasLinkedProfile) {
                e.preventDefault();
                e.stopPropagation();
                history.push(`${routesConfig.TEAM_PAGE}/${profileId}`);
                return;
              }
              if (!onViewModeProfileSelect) return;
              e.preventDefault();
              e.stopPropagation();
              lastClickedMentionKeyRef.current = target.dataset.mentionKey;
              lastClickedEntityExtractionIdRef.current =
                target.dataset.entityExtractionId || null;
              lastClickedMentionLabelRef.current =
                target.dataset.mentionLabel || null;
              lastClickedMentionIdRef.current = target.dataset.mentionId || null;
              const rect = target.getBoundingClientRect();
              setAnchorRect(rect);
              setQuery('');
              return;
            }

            e.preventDefault();
            e.stopPropagation();
            lastClickedMentionKeyRef.current = target.dataset.mentionKey;
            lastClickedEntityExtractionIdRef.current = target.dataset.entityExtractionId || null;
            lastClickedMentionLabelRef.current = target.dataset.mentionLabel || null;
            lastClickedMentionIdRef.current = target.dataset.mentionId || null;
            ignoreNextSelectionRef.current = true;
            const rect = target.getBoundingClientRect();
            setAnchorRect(rect);
            setQuery('');
          };
          rootElement.addEventListener('click', handleClick, true);
          return () => {
            rootElement.removeEventListener('click', handleClick, true);
          };
        }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          if (ignoreNextSelectionRef.current) {
            ignoreNextSelectionRef.current = false;
            return false;
          }
          const selection = window.getSelection();
            const rootEl = rootRef.current;
            const anchorNode = selection?.anchorNode || null;
            const insideRoot =
              rootEl && anchorNode ? rootEl.contains(anchorNode) : false;
            if (!selection || !selection.rangeCount || !insideRoot) {
              setAnchorRect(null);
              setQuery('');
              return false;
            }
          editor.getEditorState().read(() => {
            const lexSelection = $getSelection();
            if ($isRangeSelection(lexSelection)) {
              lastTriggerRef.current = null;
              const anchorNode = lexSelection.anchor.getNode();
              if ($isTextNode(anchorNode)) {
                const textContent = anchorNode.getTextContent();
                const caret = lexSelection.anchor.offset;
                const atIndex = textContent.lastIndexOf('@', caret - 1);
                if (atIndex !== -1) {
                  lastTriggerRef.current = {
                    nodeKey: anchorNode.getKey(),
                    start: atIndex,
                    end: caret,
                  };
                }
              }
              lastSelectionRef.current = {
                anchorKey: lexSelection.anchor.key,
                anchorOffset: lexSelection.anchor.offset,
                focusKey: lexSelection.focus.key,
                focusOffset: lexSelection.focus.offset,
              };
            }
          });
          const range = selection.getRangeAt(0);
          const text = range.startContainer.textContent || '';
          const before = text.slice(0, range.startOffset);
          const atIndex = before.lastIndexOf('@');
          const lastLineBreak = Math.max(
            before.lastIndexOf('\n'),
            before.lastIndexOf('\r')
          );

          // Require @ after the most recent newline and at least one character typed after it
          if (
            atIndex === -1 ||
            atIndex <= lastLineBreak ||
            range.startOffset - atIndex <= 1
          ) {
            setAnchorRect(null);
            setQuery('');
            return false;
          }
          setQuery(before.slice(atIndex + 1));
            const rect = getAnchorRect(range);
            if (!rect) {
              setAnchorRect(null);
              setQuery('');
              return false;
            }
            setAnchorRect(rect);
          return false;
        },
        COMMAND_PRIORITY_NORMAL
      ),
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event: KeyboardEvent) => {
          if (disabled) return false;
          if (event.key === '@') {
            // Let the character insert, then pick it up via SELECTION_CHANGE_COMMAND
            return false;
          }
          if (event.key === 'Enter') {
            // Clear any open dropdown when creating a newline
            ignoreNextSelectionRef.current = true;
            setAnchorRect(null);
            setQuery('');
            lastTriggerRef.current = null;
            return false;
          }
          if (event.key === 'Escape' && anchorRect) {
            setAnchorRect(null);
            setQuery('');
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_NORMAL
      )
    ),
    [anchorRect, disabled, editor, history, onViewModeProfileSelect]
  );

  const selectProfile = (profile: Profile | null) => {
    if (!profile) {
      setAnchorRect(null);
      setQuery('');
      return;
    }

    if (disabled) {
      const entityExtractionId =
        lastClickedEntityExtractionIdRef.current || undefined;
      lastClickedMentionKeyRef.current = null;
      lastClickedEntityExtractionIdRef.current = null;
      lastClickedMentionLabelRef.current = null;
      lastClickedMentionIdRef.current = null;
      setAnchorRect(null);
      setQuery('');
      onViewModeProfileSelect?.(profile, entityExtractionId);
      return;
    }

    editor.update(() => {
      const replaceMentionNode = (target: LexicalNode) => {
        if (!$isMentionNode(target)) return false;
        const newMention = $createMentionNode({
          id: profile.id,
          label: profile.full_name,
          profileId: profile.id,
        });
        const next = target.getNextSibling();
        const needsSpace =
          !next ||
          !($isTextNode(next) && next.getTextContent().startsWith(' '));
        target.replace(newMention);
        if (needsSpace) {
          const space = $createTextNode(' ');
          newMention.insertAfter(space);
          space.select();
        } else {
          newMention.selectNext();
        }
        return true;
      };

      // Prefer replacing the mention the user clicked
      if (lastClickedMentionKeyRef.current) {
        const remembered = $getNodeByKey(lastClickedMentionKeyRef.current);
        if (remembered && replaceMentionNode(remembered)) {
          lastClickedMentionKeyRef.current = null;
          return;
        }
      }

      let selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        const saved = lastSelectionRef.current;
        if (saved) {
          const anchorNode = $getNodeByKey(saved.anchorKey);
          const focusNode = $getNodeByKey(saved.focusKey);
          if ($isTextNode(anchorNode) && $isTextNode(focusNode)) {
            const sel = $createRangeSelection();
            sel.anchor.set(anchorNode.getKey(), saved.anchorOffset, 'text');
            sel.focus.set(focusNode.getKey(), saved.focusOffset, 'text');
            $setSelection(sel);
            selection = sel;
          }
        }
      }
      if (!$isRangeSelection(selection)) return;
      const anchorNode = selection.anchor.getNode();
      const focusNode = selection.focus.getNode();

      // If selection touches a mention, replace it directly.
      const mentionNodeFromSelection =
        selection.getNodes().find((n) => $isMentionNode(n)) ||
        ($isMentionNode(anchorNode) ? anchorNode : null) ||
        ($isMentionNode(focusNode) ? focusNode : null);

      if (mentionNodeFromSelection && $isMentionNode(mentionNodeFromSelection)) {
        if (replaceMentionNode(mentionNodeFromSelection)) {
          return;
        }
      }

      // If caret is immediately after a mention, replace that mention.
      if ($isTextNode(anchorNode)) {
        const hasActiveTrigger =
          lastTriggerRef.current?.nodeKey === anchorNode.getKey();
        const prev = anchorNode.getPreviousSibling();
        if (!hasActiveTrigger && prev && $isMentionNode(prev)) {
          if (replaceMentionNode(prev)) {
            return;
          }
        }
      }

      // Otherwise, try replacing @trigger text in a text node.
      if ($isTextNode(anchorNode)) {
        // Use remembered trigger if available and same node
        if (
          lastTriggerRef.current &&
          lastTriggerRef.current.nodeKey === anchorNode.getKey()
        ) {
          const { start, end } = lastTriggerRef.current;
          const text = anchorNode.getTextContent();
          const before = text.slice(0, start);
          const after = text.slice(end);
          const beforeNode = $createTextNode(before);
          const mentionNode = $createMentionNode({
            id: profile.id,
            label: profile.full_name,
            profileId: profile.id,
          });
          const needsSpace =
            after.length === 0 || !/^[\s.,!?]/.test(after);
          const afterNode = $createTextNode(`${needsSpace ? ' ' : ''}${after}`);
          anchorNode.replace(beforeNode);
          beforeNode.insertAfter(mentionNode);
          mentionNode.insertAfter(afterNode);
          afterNode.select();
          lastTriggerRef.current = null;
          return;
        }

        // Fallback: search for @ before caret
        const text = anchorNode.getTextContent();
        const anchorOffset = selection.anchor.offset;
        const atIndex = text.lastIndexOf('@', anchorOffset);
        if (atIndex !== -1) {
          const before = text.slice(0, atIndex);
          const after = text.slice(anchorOffset);
          const beforeNode = $createTextNode(before);
          const mentionNode = $createMentionNode({
            id: profile.id,
            label: profile.full_name,
            profileId: profile.id,
          });
          const needsSpace =
            after.length === 0 || !/^[\s.,!?]/.test(after);
          const afterNode = $createTextNode(`${needsSpace ? ' ' : ''}${after}`);
          anchorNode.replace(beforeNode);
          beforeNode.insertAfter(mentionNode);
          mentionNode.insertAfter(afterNode);
          afterNode.select();
          lastTriggerRef.current = null;
          return;
        }
      }

      // Fallback: insert mention at current selection with a trailing space.
      const mentionNode = $createMentionNode({
        id: profile.id,
        label: profile.full_name,
        profileId: profile.id,
      });
      const spaceNode = $createTextNode(' ');
      selection.insertNodes([mentionNode, spaceNode]);
      spaceNode.select();

      // As a last resort, try replacing any mention in the doc
      const anyMention = findAnyMention();
      if (anyMention && replaceMentionNode(anyMention)) {
        return;
      }
    });

    setAnchorRect(null);
    setQuery('');
  };

  if (!anchorRect) return null;

  return <>{dropdownRenderer(anchorRect, query, selectProfile, lastClickedEntityExtractionIdRef.current || undefined, lastClickedMentionLabelRef.current || undefined, lastClickedMentionIdRef.current || undefined)}</>;
}

